<template>
    <div class="h-full w-full flex flex-col">
        <div class="flex justify-between flex-wrap mb-4">
            <h1 class="text-base md:text-lg font-semibold">
                Initiated Requests
            </h1>
            <div class="w-full sm:w-auto mb-4">
                <search @on-search="onSearch" class-list="px-4 h-8"></search>
            </div>
        </div>

        <div class="flex flex-col" v-if="loading">
            <table-shimmer :titleHeaders="[
                '#',
                'Speaker/Representative',
                'Event',
                'Start Date',
                'End Date',
                'Entity/Location',
                'Requester',
                'Supervisor',
                'Status',
                'Created At',
                'Actions',
            ]" :rows="first"></table-shimmer>
        </div>
        <div v-else-if="
            !loading && !hasItems
        " class="h-full flex justify-center items-center py-12">
            <div class="text-center max-w-md">
                <div class="mb-6">
                    <div
                        class="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                        <font-awesome-icon :icon="['fas', 'file-alt']" class="h-12 w-12 text-blue-500" />
                    </div>
                </div>
                <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Initiated Requests Found</h2>
                <p class="text-gray-500 mb-6 leading-relaxed">
                    You haven't initiated any security clearance requests yet. Click the button below to create your
                    first initiation request.
                </p>
                <button v-if="hasSuperPermission('security-clearance-initiate')" @click="openInitiateModal"
                    class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium shadow-lg">
                    <font-awesome-icon :icon="['fas', 'plus']" class="h-4 w-4 mr-2" />
                    Initiate Security Clearance
                </button>
            </div>
        </div>
        <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
                <div class="inline-block min-w-full py-2">
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-left text-sm font-light">
                            <thead class="border-b font-medium dark:border-neutral-500">
                                <tr>
                                    <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                        #
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Speaker/Representative
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Event
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Start Date
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        End Date
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Entity/Location
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Requester
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Supervisor
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Status
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Created At
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in items" :key="index"
                                    class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                                    <td class="whitespace-nowrap px-6 py-2 font-medium">
                                        {{ paginatorInfo.firstItem + index }}
                                    </td>
                                    <td class="px-6 py-2">
                                        {{ item.speaker }}
                                    </td>
                                    <td class="px-6 py-2">
                                        <div class="max-w-xs truncate" :title="item.event">
                                            {{ item.event }}
                                        </div>
                                    </td>
                                    <td class="whitespace-nowrap px-6 py-2">
                                        {{ getFormattedDateTime(item.date_time) }}
                                    </td>
                                    <td class="whitespace-nowrap px-6 py-2">
                                        {{ item.end_date ? getFormattedDateTime(item.end_date) : '-' }}
                                    </td>
                                    <td class="px-6 py-2">
                                        {{ item.entity_location }}
                                    </td>
                                    <td class="px-6 py-2">
                                        {{ item.requester }}
                                    </td>
                                    <td class="px-6 py-2">
                                        {{ item.supervisor }}
                                    </td>
                                    <td class="px-6 py-2" v-html="getStatus(item)"></td>
                                    <td class="whitespace-nowrap px-6 py-2">
                                        {{ getFormattedDate(item.created_at) }}
                                    </td>
                                    <td class="px-6 py-2">
                                        <div class="flex">
                                            <div @click.stop="viewItem(item)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                title="View Initiation Details">
                                                <icon-eye
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                            </div>
                                            <div v-if="item.securityClearance && item.securityClearance.token"
                                                @click.stop="viewSecurityClearance(item.securityClearance)"
                                                class="bg-gray-100 hover:bg-green-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                title="View Security Clearance">
                                                <icon-document
                                                    class="w-4 h-4 transform group-hover:text-green-600 group-hover:scale-110 hover:scale-110"></icon-document>
                                            </div>
                                            <div v-if="item.securityClearance && item.securityClearance.token"
                                                @click.stop="generatePdf(item.securityClearance.token)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                title="Print Security Clearance">
                                                <icon-printer
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-printer>
                                            </div>
                                            <div v-if="item.token" @click.stop="copyLink(item.token)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer relative"
                                                :title="copiedToken === item.token ? 'Link Copied!' : 'Copy Link'">
                                                <transition name="icon-fade" mode="out-in">
                                                    <icon-copied v-if="copiedToken === item.token"
                                                        class="w-4 h-4"></icon-copied>
                                                    <icon-copy v-else class="w-4 h-4"></icon-copy>
                                                </transition>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="mt-4">
                <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-modal v-if="isViewItem" :title="`View Initiation Details`" @close="closeModal">
                <div class="px-8 py-4">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Speaker /
                                Representative</label>
                            <p class="text-gray-900">{{ selectedItem.speaker }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Event</label>
                            <p class="text-gray-900">{{ selectedItem.event }}</p>
                        </div>
                        <div class="sm:col-span-2">
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Purpose of Event</label>
                            <p class="text-gray-900">{{ selectedItem.purpose_of_event }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Target Group</label>
                            <p class="text-gray-900">{{ selectedItem.target_group }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Age Group</label>
                            <p class="text-gray-900">{{ selectedItem.age_group }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                            <p class="text-gray-900">{{ getFormattedDateTime(selectedItem.date_time) }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                            <p class="text-gray-900">{{ selectedItem.end_date ?
                                getFormattedDateTime(selectedItem.end_date) : '-' }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Entity/Location</label>
                            <p class="text-gray-900">{{ selectedItem.entity_location }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Requester</label>
                            <p class="text-gray-900">{{ selectedItem.requester }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Supervisor</label>
                            <p class="text-gray-900">{{ selectedItem.supervisor }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                            <p class="text-gray-900" v-html="getStatus(selectedItem)"></p>
                        </div>
                        <div class="sm:col-span-2">
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Send to Email</label>
                            <p class="text-gray-900">{{ selectedItem.send_to_email }}</p>
                        </div>
                        <div class="sm:col-span-2" v-if="selectedItem.token">
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Initiation Link</label>
                            <div class="flex items-center gap-2">
                                <input type="text" :value="getInitiationLink(selectedItem.token)" readonly
                                    class="flex-1 border rounded-lg py-2 px-3 text-sm bg-gray-50" />
                                <button @click="copyLink(selectedItem.token)" type="button" :class="[
                                    'w-28 flex justify-center items-center border-2 font-medium rounded-lg px-4 py-2 uppercase text-xs cursor-pointer transition-all duration-200 flex items-center gap-2',
                                    copiedToken === selectedItem.token
                                        ? 'border-gray-600 bg-gray-600 text-white'
                                        : 'border-ternary text-white hover:text-ternary bg-ternary hover:bg-white'
                                ]">
                                    <transition name="icon-fade" mode="out-in">
                                        <icon-copied v-if="copiedToken === selectedItem.token"
                                            class="w-4 h-4"></icon-copied>
                                        <icon-copy v-else class="w-4 h-4"></icon-copy>
                                    </transition>
                                    <span>{{ copiedToken === selectedItem.token ? 'Copied!' : 'Copy' }}</span>
                                </button>
                            </div>
                        </div>
                        <div class="sm:col-span-2" v-else>
                            <label class="block text-sm font-semibold text-gray-700 mb-1">Initiation Link</label>
                            <p class="text-gray-500 text-sm">Link is no longer available for this initiation.</p>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewSecurityClearance" :title="`View Security Clearance`"
                @close="closeSecurityClearanceModal">
                <view-security-clearance class="text-left" :item="selectedSecurityClearance"
                    :from-initiated-requests="true" :iframe-height="getIframeHeight"
                    @close="closeSecurityClearanceModal"></view-security-clearance>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import TableShimmer from "../../../components/shared/TableShimmer.vue";
import Pagination from "../../../components/shared/pagination.vue";
import ViewSecurityClearance from "../view.vue";
import IconEye from "../../../components/icons/IconEye.vue";
import IconDocument from "../../../components/icons/IconDocument.vue";
import IconCopy from "../../../components/icons/IconCopy.vue";
import IconCopied from "../../../components/icons/IconCopied.vue";
import IconPrinter from "../../../components/icons/IconPrinter.vue";
import { formattedDate, formattedDateWithTimeAmPm } from "../../../helpers/utils";
import Search from "../../../components/shared/search.vue";

export default {
    name: "InitiatedRequests",
    components: {
        TableShimmer,
        Pagination,
        ViewSecurityClearance,
        IconEye,
        IconDocument,
        IconCopy,
        IconCopied,
        IconPrinter,
        Search,
    },
    data() {
        return {
            initiatedRequests: {
                data: [],
                paginatorInfo: {}
            },
            first: 10,
            page: 1,
            searchText: "",
            selectedItem: null,
            isViewItem: false,
            selectedSecurityClearance: null,
            isViewSecurityClearance: false,
            copiedToken: null,
            loading: false,
        };
    },
    watch: {
        searchText() {
            this.page = 1;
            this.fetchInitatedRequests();
        },
        page() {
            this.fetchInitatedRequests();
        },
    },
    computed: {
        items() {
            return this.initiatedRequests.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.initiatedRequests.paginatorInfo;
        },
    },
    mounted() {
        if (this.hasSuperPermission('security-clearance-initiate')) {
            this.fetchInitatedRequests();
        }
    },
    methods: {
        async fetchInitatedRequests() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetInitiatedRequests(
                            $first: Int!
                            $page: Int!
                            $search: String
                        ) {
                            initiatedRequests(
                                first: $first
                                page: $page
                                search: $search
                            ) {
                                error
                                error_code
                                message
                                data {
                                    id
                                    token
                                    speaker
                                    event
                                    purpose_of_event
                                    target_group
                                    age_group
                                    date_time
                                    end_date
                                    entity_location
                                    requester
                                    supervisor
                                    send_to_email
                                    created_at
                                    securityClearance {
                                        token
                                        status
                                        note
                                    }
                                }
                                paginatorInfo {
                                    count
                                    currentPage
                                    firstItem
                                    hasMorePages
                                    lastItem
                                    lastPage
                                    perPage
                                    total
                                }
                            }
                        }
                    `,
                    variables: {
                        first: this.first,
                        page: this.page,
                        search: this.searchText || null,
                    },
                    fetchPolicy: "network-only",
                });

                if (data.initiatedRequests.error) {
                    if (data.initiatedRequests.error_code == 403) {
                        this.$router.replace("/no-permission");
                    } else if (data.initiatedRequests.error_code == 401) {
                        this.$router.replace("/");
                    } else {
                        alert(data.initiatedRequests.message);
                        this.$router.replace("/");
                    }
                } else {
                    this.initiatedRequests = data.initiatedRequests;
                }
            } catch (error) {
                console.error("Error fetching initiated requests:", error);
            } finally {
                this.loading = false;
            }
        },
        onSearch(searchText) {
            this.searchText = searchText;
        },
        setPage(page) {
            this.page = page;
        },
        getFormattedDate(date) {
            return formattedDate(date);
        },
        getFormattedDateTime(dateTime) {
            if (!dateTime) return "";
            return formattedDateWithTimeAmPm(dateTime);
        },
        getStatus(item) {
            if (!item || !item.securityClearance) {
                return '<div class="text-blue-600 font-medium">Request Initiated</div>';
            }
            const status = item.securityClearance.status;
            const note = item.securityClearance.note;
            if (status === null) {
                return '<div class="text-yellow-600 font-medium">Under Review</div>';
            } else if (status === true) {
                return '<div class="text-green-600 font-medium">Approved</div>';
            } else {
                if (note) {
                    return `<div class="flex flex-col"><div class="text-red-600 font-medium">Rejected</div><div class="text-xs text-gray-600 mt-1">Note: ${note}</div></div>`;
                } else {
                    return '<div class="text-red-600 font-medium">Rejected</div>';
                }
            }
        },
        viewItem(item) {
            this.selectedItem = item;
            this.isViewItem = true;
        },
        closeModal() {
            this.isViewItem = false;
            this.selectedItem = null;
            this.copiedToken = null;
        },
        viewSecurityClearance(securityClearance) {
            this.selectedSecurityClearance = { token: securityClearance.token };
            this.isViewSecurityClearance = true;
        },
        closeSecurityClearanceModal() {
            this.isViewSecurityClearance = false;
            this.selectedSecurityClearance = null;
        },
        getIframeHeight() {
            return `${window.innerHeight}px`;
        },
        getInitiationLink(token) {
            if (token) {
                return process.env.VUE_APP_FRONTEND_URL + 'security-clearance-form/' + token;
            }
            return '';
        },
        async copyLink(token) {
            if (!token) {
                return;
            }
            const link = this.getInitiationLink(token);
            try {
                await navigator.clipboard.writeText(link);
                this.copiedToken = token;
                setTimeout(() => {
                    this.copiedToken = null;
                }, 2500);
            } catch (err) {
                const textArea = document.createElement('textarea');
                textArea.value = link;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    this.copiedToken = token;
                    setTimeout(() => {
                        this.copiedToken = null;
                    }, 2500);
                } catch (err) {
                    console.error('Failed to copy link:', err);
                }
                document.body.removeChild(textArea);
            }
        },
        generatePdf(token) {
            this.$emit('generate-pdf', { token: token, fromInitiatedRequests: true });
        },
        openInitiateModal() {
            this.$emit('open-initiate-modal');
        },
        refresh() {
            this.fetchInitatedRequests();
        },
        hasSuperPermission(crudName) {
            const permissions = this.$store.getters.permissions;
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.icon-fade-enter-active,
.icon-fade-leave-active {
    transition: all 0.2s ease-in-out;
}

.icon-fade-enter-from {
    opacity: 0;
    transform: scale(0.9);
}

.icon-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
</style>
